import React, { useMemo, useState, forwardRef, useImperativeHandle } from "react";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { useParams } from "react-router-dom";
import MeaCreateModal from "../../../../components/MeaCreateModal";
import MeaStudentAvatar from "../../components/MeaStudentAvatar";
import slotChangeRequestsAPI from "../../../../services/slot_change_requests";
import studentsAPI from "../../../../services/students";
import slotsAPI from "../../../../services/slots";
import classesAPI from "../../../../services/classes";
import scheduleAPI from "../../../../services/schedule";
import {
  buildPreferredSlotOptions,
  combineDateAndTime,
  formatSlotLabel,
  slotsForClass,
  studentClassId,
  studentDisplayName,
  studentLevelId,
  timeFromDate,
} from "../slotChangeUtils";
import { PERMISSION_TRAINING_STUDENTS } from "../../../../permissions";
import SlotPreferencePicker from "./SlotPreferencePicker";

const CreateSlotChangeRequestModal = forwardRef((props, ref) => {
  const params = useParams();
  const schoolId = props.schoolId || params.schoolId;
  const trainingId = props.trainingId || params.trainingId;
  const accessToken = props.accessToken || "";
  const availableStudents = props.students;
  const hideRequestedAtFields = props.hideRequestedAtFields === true;
  const hideUrgency = props.hideUrgency === true;
  const fixedRequestedAtToNow = props.fixedRequestedAtToNow === true;

  const innerRef = React.useRef();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [preferredSlotIds, setPreferredSlotIds] = useState([]);
  const [requestedDate, setRequestedDate] = useState(new Date());
  const [requestedTime, setRequestedTime] = useState(timeFromDate(new Date()));
  const [urgency, setUrgency] = useState("NORMAL");
  const [reason, setReason] = useState("");
  const [lockStudent, setLockStudent] = useState(!!props.lockStudent);

  const currentLevelId = studentLevelId(selectedStudent);
  const currentClassId = studentClassId(selectedStudent);
  const currentClassName =
    selectedStudent?.classe?.name || selectedStudent?.class?.name || "";

  const studentsQuery = studentsAPI.useGetOngoingList(
    schoolId,
    trainingId,
    accessToken,
    1,
    500,
    {}
  );
  const slotsQuery = slotsAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000
  );
  const classesQuery = classesAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    { level_id: currentLevelId }
  );
  const scheduleQuery = scheduleAPI.useGetList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    {}
  );

  const studentOptions = useMemo(
    () =>
      (availableStudents || studentsQuery?.data?.data || [])
        .filter((student) => studentClassId(student))
        .map((student) => ({
          ...student,
          label:
            studentDisplayName(student) +
            (student?.classe?.name ? ` — ${student.classe.name}` : ""),
        })),
    [availableStudents, studentsQuery?.data?.data]
  );

  const slotOptions = useMemo(() => {
    if (!selectedStudent || !currentLevelId) {
      return [];
    }
    return buildPreferredSlotOptions({
      classes: classesQuery?.data?.data,
      slots: slotsQuery?.data?.data,
      scheduleItems: scheduleQuery?.data?.data,
      currentClassId,
      levelId: currentLevelId,
    });
  }, [
    selectedStudent,
    currentLevelId,
    currentClassId,
    classesQuery?.data?.data,
    slotsQuery?.data?.data,
    scheduleQuery?.data?.data,
  ]);

  const currentSlots = useMemo(
    () =>
      slotsForClass({
        slots: slotsQuery?.data?.data,
        scheduleItems: scheduleQuery?.data?.data,
        classId: currentClassId,
      }),
    [slotsQuery?.data?.data, scheduleQuery?.data?.data, currentClassId]
  );

  const preferredSlotsLoading =
    !!selectedStudent &&
    (slotsQuery?.isLoading || classesQuery?.isLoading || scheduleQuery?.isLoading);

  const resetForm = (student, locked) => {
    setSelectedStudent(student || null);
    setPreferredSlotIds([]);
    const now = new Date();
    setRequestedDate(now);
    setRequestedTime(timeFromDate(now));
    setUrgency("NORMAL");
    setReason("");
    setLockStudent(!!locked);
  };

  useImperativeHandle(ref, () => ({
    show: (callback, student) => {
      const nextStudent = student || props.student || null;
      resetForm(nextStudent, !!student || !!props.lockStudent);
      innerRef.current.show(callback);
    },
  }));

  const handleStudentChange = (student) => {
    setSelectedStudent(student);
    setPreferredSlotIds([]);
  };

  const requestedAtValue = fixedRequestedAtToNow
    ? new Date().toISOString()
    : combineDateAndTime(requestedDate, requestedTime);

  return (
    <MeaCreateModal
      ref={innerRef}
      title="Nouvelle demande de changement de créneau"
      schoolId={schoolId}
      trainingId={trainingId}
      accessToken={accessToken}
      serviceAPI={slotChangeRequestsAPI}
      submitDisabled={!selectedStudent || preferredSlotsLoading}
      data={{
        student_id: selectedStudent?.id,
        preferred_slot_ids: preferredSlotIds,
        requested_at: requestedAtValue,
        urgency: hideUrgency ? "NORMAL" : urgency,
        reason: reason || null,
      }}
    >
      {selectedStudent ? (
        <div className="mb-3">
          <label className="block text-900 font-medium mb-2">Élève</label>
          <MeaStudentAvatar
            student={selectedStudent}
            schoolId={schoolId}
            trainingId={trainingId}
            permission={PERMISSION_TRAINING_STUDENTS.GET}
          />
          <small className="block text-600 mt-2">
            {currentClassName && (
              <span className="block">
                Classe actuelle :{" "}
                <span className="font-medium text-900">{currentClassName}</span>
              </span>
            )}
            <span className="block">
              Créneau actuel :{" "}
              <span className="font-medium text-900">
                {currentSlots.length
                  ? currentSlots.map((slot) => formatSlotLabel(slot)).join(", ")
                  : "Aucun créneau"}
              </span>
            </span>
          </small>
          {!lockStudent && (
            <Dropdown
              value={studentOptions.find((s) => s.id === selectedStudent?.id)}
              onChange={(e) => handleStudentChange(e.value)}
              options={studentOptions}
              optionLabel="label"
              filter
              placeholder="Changer d'élève"
              className="w-full mt-2"
            />
          )}
        </div>
      ) : (
        <>
          <label className="block text-900 font-medium mb-2">Élève</label>
          <Dropdown
            value={studentOptions.find((s) => s.id === selectedStudent?.id)}
            onChange={(e) => handleStudentChange(e.value)}
            options={studentOptions}
            optionLabel="label"
            filter
            placeholder="Sélectionner un élève"
            className="w-full mb-3"
          />
        </>
      )}

      <label className="block text-900 font-medium mb-2">
        Créneaux souhaités (par ordre de préférence)
      </label>
      <SlotPreferencePicker
        options={slotOptions}
        value={preferredSlotIds}
        onChange={setPreferredSlotIds}
        disabled={!selectedStudent || preferredSlotsLoading}
        placeholder={
          !selectedStudent
            ? "Sélectionner un élève d'abord"
            : preferredSlotsLoading
              ? "Chargement des créneaux disponibles..."
              : slotOptions.length
                ? "Ajouter un créneau"
                : "Aucun autre créneau du même niveau"
        }
      />
      {selectedStudent && !preferredSlotsLoading && !slotOptions.length ? (
        <small className="block text-600 mb-3">
          Aucun autre créneau du même niveau n'est disponible. La demande peut
          quand même être créée pour permettre un refus motivé et l'envoi d'un
          mail aux parents.
        </small>
      ) : null}

      {!hideRequestedAtFields && (
        <div className="flex flex-column md:flex-row gap-3 mb-3">
          <div className="flex-1">
            <label className="block text-900 font-medium mb-2">Date de la demande</label>
            <Calendar
              value={requestedDate}
              onChange={(e) => setRequestedDate(e.value)}
              locale="fr"
              dateFormat="dd/mm/yy"
              showIcon
              showOnFocus={false}
              className="w-full"
            />
          </div>
          <div style={{ minWidth: "8rem" }}>
            <label className="block text-900 font-medium mb-2">Heure</label>
            <InputText
              type="time"
              value={requestedTime}
              onChange={(e) => setRequestedTime(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      )}

      {!hideUrgency && (
        <>
          <label className="block text-900 font-medium mb-2">Urgence</label>
          <Dropdown
            value={urgency}
            onChange={(e) => setUrgency(e.value)}
            options={[
              { label: "Neutre", value: "NORMAL" },
              { label: "Prioritaire", value: "HIGH" },
              { label: "Urgent", value: "URGENT" },
            ]}
            className="w-full mb-3"
          />
        </>
      )}

      <label className="block text-900 font-medium mb-2">Motif</label>
      <InputTextarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        className="w-full mb-3"
        placeholder="Contrainte familiale, transport..."
      />
    </MeaCreateModal>
  );
});

export default CreateSlotChangeRequestModal;
